import type { WorkOrderFiltersForm } from "../../../../features/work-order";
import type { ISelectOption } from "../../../../shared/types/ISelectOption";

/**
 * Keys of the filter form rendered as a plain `<input>`.
 *
 * `sortField`/`sortDirection` are string unions and would otherwise match the `extends string`
 * test — they are sort state, not filter values, so they are excluded by name.
 */
export type WorkOrderTextFilterKey = Exclude<
  {
    [K in keyof WorkOrderFiltersForm]: WorkOrderFiltersForm[K] extends string ? K : never;
  }[keyof WorkOrderFiltersForm],
  "sortField" | "sortDirection"
>;

export interface WorkOrderFilterField {
  key: WorkOrderTextFilterKey;
  label: string;
  sortKey: WorkOrderFiltersForm["sortField"];
  /** `<input type>` — only `datum` needs anything other than text. */
  type?: string;
  /**
   * Which on-screen keyboard a phone opens. The digit-only filters stay `type="text"` — `number`
   * would add spinners and reject a leading zero — and ask for the numeric pad this way instead.
   */
  inputMode?: "numeric";
}

/**
 * The text filters, in the order the desktop columns use. Split around Gewerk, which is a search
 * select rather than an input and so cannot live in the same list.
 *
 * Both layouts read their labels from here: the mobile panel orders them differently, and a label
 * that drifted between the two would name the same column two ways depending on the screen.
 */
export const WORK_ORDER_FILTER_FIELDS_BEFORE_GEWERK: WorkOrderFilterField[] = [
  { key: "workOrderId", label: "Auftrag-ID", sortKey: "workOrderId", inputMode: "numeric" },
  { key: "mieter", label: "Mieter", sortKey: "mieter" },
  { key: "handwerker", label: "Handwerker", sortKey: "handwerker" },
];

export const WORK_ORDER_FILTER_FIELDS_AFTER_GEWERK: WorkOrderFilterField[] = [
  { key: "datum", label: "Datum", sortKey: "datum", type: "date" },
  { key: "ve", label: "VE", sortKey: "ve", inputMode: "numeric" },
  { key: "hauseingang", label: "Gebäude", sortKey: "hauseingang" },
  { key: "wohnung", label: "Wohnung", sortKey: "wohnung", inputMode: "numeric" },
];

export const WORK_ORDER_FILTER_FIELDS: WorkOrderFilterField[] = [
  ...WORK_ORDER_FILTER_FIELDS_BEFORE_GEWERK,
  ...WORK_ORDER_FILTER_FIELDS_AFTER_GEWERK,
];

const FIELD_BY_KEY = new Map(WORK_ORDER_FILTER_FIELDS.map((field) => [field.key, field]));

export const workOrderFilterField = (key: WorkOrderTextFilterKey): WorkOrderFilterField => {
  const field = FIELD_BY_KEY.get(key);
  if (!field) throw new Error(`Unknown work-order filter field: ${key}`);
  return field;
};

/** Sortable columns for the mobile sort picker — the desktop sorts by tapping a column header. */
export const WORK_ORDER_SORT_OPTIONS: ISelectOption[] = WORK_ORDER_FILTER_FIELDS.map((field) => ({
  value: field.sortKey,
  label: field.label,
}));

/**
 * How many filters are narrowing the list. Drives the badge on the collapsed mobile panel — with
 * the fields hidden, it is the only thing telling you the list is not showing everything.
 */
export const countActiveWorkOrderFilters = (filters: WorkOrderFiltersForm): number => {
  const textCount = WORK_ORDER_FILTER_FIELDS.filter(
    (field) => filters[field.key].trim() !== "",
  ).length;

  return textCount + (filters.status ? 1 : 0) + (filters.gewerkId !== null ? 1 : 0);
};
